import React from "react";
import { Link } from "react-router-dom";
import { QrCode, CheckCircle2, ArrowRight, Wrench, ShieldCheck, Bell } from "lucide-react";
import Reveal from "@/components/Reveal";

// Static SVG "QR code" glyph — decorative mockup only
const QrGlyph = () => (
  <svg viewBox="0 0 100 100" className="w-full h-full" aria-hidden="true">
    <rect x="0" y="0" width="100" height="100" fill="#ffffff" />
    {[
      [4, 4], [4, 12], [4, 20], [4, 28], [4, 36], [12, 4], [20, 4], [28, 4], [36, 4],
      [36, 12], [36, 20], [36, 28], [36, 36], [12, 36], [20, 36], [28, 36],
      [12, 12], [20, 20], [28, 12], [12, 20], [28, 28], [20, 28],
      [60, 4], [68, 4], [76, 4], [84, 4], [92, 4], [92, 12], [92, 20], [92, 28], [92, 36],
      [60, 36], [68, 36], [76, 36], [84, 36], [60, 12], [60, 20], [60, 28],
      [68, 12], [76, 20], [84, 28], [68, 28], [76, 12],
      [4, 60], [12, 60], [20, 60], [28, 60], [36, 60], [4, 68], [4, 76], [4, 84], [4, 92],
      [36, 68], [36, 76], [36, 84], [36, 92], [12, 92], [20, 92], [28, 92],
      [12, 68], [20, 76], [28, 84], [20, 68], [28, 76], [12, 84],
      [48, 48], [56, 48], [64, 56], [72, 48], [80, 64], [88, 56], [48, 56], [64, 64],
      [72, 72], [80, 80], [56, 80], [48, 88], [72, 88], [88, 88], [64, 88],
      [48, 72], [56, 64], [80, 48], [88, 72], [64, 80], [72, 56], [80, 88], [88, 48],
    ].map(([x, y], i) => (
      <rect key={i} x={x} y={y} width="8" height="8" fill="#0f2c46" />
    ))}
  </svg>
);

const StatusPill = ({ tone = "ok", label }) => {
  const map = {
    ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
    warn: "bg-amber-50 text-amber-700 border-amber-200",
    due: "bg-rose-50 text-rose-700 border-rose-200",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono tracking-wide uppercase px-2 py-1 rounded-full border ${map[tone]}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${tone === "ok" ? "bg-emerald-500" : tone === "warn" ? "bg-amber-500" : "bg-rose-500"}`} />
      {label}
    </span>
  );
};

const ComplianceOsMockup = () => {
  return (
    <section className="py-24 bg-ink text-white">
      <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
          {/* Left — copy */}
          <div className="lg:col-span-5">
            <div className="font-mono text-[11px] tracking-overline uppercase text-brand mb-6">
              [ Compliance OS · Digital Asset Tracker ]
            </div>
            <h2 className="font-display font-bold text-[2.25rem] md:text-[3rem] leading-[1.02] tracking-tightest mb-6">
              Every asset,
              <span className="text-brand"> a scan away.</span>
            </h2>
            <p className="text-[17px] text-white/70 leading-relaxed mb-8">
              We tag every damper, AHU, extract fan and boiler on your site with a QR code. One scan
              opens the full service history, drawings, next PPM date and compliance status &mdash;
              for you, your FM team and your auditor.
            </p>

            <ul className="space-y-3 mb-10">
              {[
                ["QR-scannable equipment records", "Wrench"],
                ["Automated PPM reminders (TR/19, BS 9999, F-Gas, TM44)", "Bell"],
                ["Audit-ready compliance reports on demand", "ShieldCheck"],
                ["One asset register across all your buildings", "CheckCircle2"],
              ].map(([label], i) => (
                <li key={i} className="flex items-start gap-3 text-[14px] text-white/80">
                  <CheckCircle2 className="h-4 w-4 text-brand mt-0.5 flex-shrink-0" />
                  <span>{label}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-3">
              <Link
                to="/compliance-os?portal=client"
                data-testid="compliance-os-portal-cta"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-brand text-white font-semibold text-[14px] rounded-sm hover:bg-accent transition-colors"
              >
                Client portal login <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="compliance-os-demo-cta"
                className="inline-flex items-center gap-2 px-6 py-3.5 border border-white/20 text-white font-semibold text-[14px] rounded-sm hover:bg-white/10 transition-colors"
              >
                Request a walkthrough
              </Link>
            </div>
            <div className="mt-4 font-mono text-[10px] tracking-overline uppercase text-white/40">
              Portal launching Q2 2026 · Preview mockup shown right
            </div>
          </div>

          {/* Right — mockup */}
          <div className="lg:col-span-7">
            <Reveal>
              <div className="relative">
                {/* Browser frame */}
                <div className="rounded-lg overflow-hidden shadow-2xl bg-white text-ink border border-white/10">
                  <div className="flex items-center gap-2 px-4 py-3 bg-paper-cool border-b border-black/8">
                    <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                    <div className="ml-3 flex-1 flex items-center">
                      <div className="text-[11px] font-mono text-muted-foreground bg-white border border-black/8 rounded-sm px-3 py-1 truncate">
                        os.calmbreeze.co.uk / assets / AHU-04-B2
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
                    {/* Left rail */}
                    <div className="md:col-span-2 bg-paper-cool p-5 border-r border-black/8">
                      <div className="aspect-square bg-white rounded-sm p-3 border border-black/8 mb-4">
                        <QrGlyph />
                      </div>
                      <div className="font-mono text-[10px] tracking-overline uppercase text-muted mb-1">Asset ID</div>
                      <div className="font-display font-semibold text-[1.05rem] mb-3 tracking-tight">AHU-04-B2</div>
                      <div className="text-[12px] text-muted-foreground leading-relaxed">
                        Air Handling Unit 04 · Basement 2 · Reading Campus
                      </div>
                      <div className="mt-4">
                        <StatusPill tone="ok" label="Compliant" />
                      </div>
                    </div>

                    {/* Right pane */}
                    <div className="md:col-span-3 p-5">
                      <div className="flex items-center justify-between mb-4">
                        <div className="font-display font-semibold text-[1rem] tracking-tight">Service history</div>
                        <span className="font-mono text-[10px] tracking-wide uppercase text-muted">Last 4</span>
                      </div>
                      <div className="space-y-3">
                        {[
                          { date: "12 Jan 2026", task: "Quarterly PPM · filters replaced", icon: <Wrench className="h-3.5 w-3.5" />, tone: "ok" },
                          { date: "08 Oct 2025", task: "Belt tension check", icon: <Wrench className="h-3.5 w-3.5" />, tone: "ok" },
                          { date: "14 Jul 2025", task: "F-Gas leak test passed", icon: <ShieldCheck className="h-3.5 w-3.5" />, tone: "ok" },
                          { date: "22 Apr 2025", task: "Coil clean · fin comb", icon: <Wrench className="h-3.5 w-3.5" />, tone: "ok" },
                        ].map((row, i) => (
                          <div key={i} className="flex items-center justify-between border-b border-black/5 pb-3 last:border-0">
                            <div className="flex items-start gap-3">
                              <div className="h-7 w-7 rounded-full bg-brand/10 text-brand flex items-center justify-center flex-shrink-0">
                                {row.icon}
                              </div>
                              <div>
                                <div className="text-[13px] font-medium">{row.task}</div>
                                <div className="text-[11px] text-muted-foreground font-mono">{row.date}</div>
                              </div>
                            </div>
                            <StatusPill tone={row.tone} label="Done" />
                          </div>
                        ))}
                      </div>

                      <div className="mt-5 pt-4 border-t border-black/8 grid grid-cols-2 gap-3">
                        <div className="p-3 rounded-sm bg-paper-cream">
                          <div className="flex items-center gap-2 mb-1">
                            <Bell className="h-3.5 w-3.5 text-brand" />
                            <div className="font-mono text-[10px] tracking-overline uppercase text-muted">Next PPM</div>
                          </div>
                          <div className="text-[13px] font-medium">14 Apr 2026</div>
                        </div>
                        <div className="p-3 rounded-sm bg-paper-cream">
                          <div className="flex items-center gap-2 mb-1">
                            <QrCode className="h-3.5 w-3.5 text-brand" />
                            <div className="font-mono text-[10px] tracking-overline uppercase text-muted">Last scan</div>
                          </div>
                          <div className="text-[13px] font-medium">Today · 09:42</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating badge */}
                <div className="hidden md:block absolute -bottom-6 -left-6 bg-brand text-white rounded-md p-4 shadow-xl max-w-[220px]">
                  <div className="font-mono text-[10px] tracking-overline uppercase text-white/70 mb-1">Live tally</div>
                  <div className="font-display font-bold text-[1.75rem] leading-none">560</div>
                  <div className="text-[11px] text-white/80 mt-1">Fire dampers tagged &amp; tracked at University of Reading</div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComplianceOsMockup;
